import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, User, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const DisplayNameModal = ({ open, onComplete, defaultName, API }) => {
  const [displayName, setDisplayName] = useState(defaultName || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!displayName.trim()) {
      toast.error("Please enter a display name");
      return;
    }
    if (displayName.trim().length < 2) {
      toast.error("Display name must be at least 2 characters");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/auth/set-display-name?display_name=${encodeURIComponent(displayName.trim())}`
      );
      toast.success("Display name set!");
      onComplete(displayName.trim());
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save display name");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="glass border-gray-800 text-white max-w-md [&>button]:hidden sm:max-h-[90vh] max-h-[85vh] overflow-y-auto mx-4 sm:mx-auto"
        style={{ 
          // Ensure dialog is positioned properly on mobile
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 flex items-center justify-center">
                <User className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
              </div>
            </div>
          </div>
          <DialogTitle className="text-lg sm:text-xl text-center text-white">
            Welcome to AfterPlay! 🎵
          </DialogTitle>
          <DialogDescription className="text-center text-gray-400 text-sm sm:text-base">
            What name would you like to go by? This will be shown publicly on your submissions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
          <div className="space-y-2">
            <Label htmlFor="displayName" className="text-gray-300 text-sm sm:text-base">
              Display Name / Artist Alias
            </Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., DJ Gumbo, The Producer, etc."
              className="bg-slate-800/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500 text-base"
              maxLength={50}
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
            />
            <p className="text-xs text-gray-500">
              Your real name stays private. This is what others will see.
            </p>
          </div>
        </div>

        {/* Submit button - prominent and always visible */}
        <div className="pt-2 pb-2 sm:pb-4 sticky bottom-0 bg-inherit">
          <Button
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-4 sm:py-6 text-base sm:text-lg rounded-xl shadow-lg shadow-purple-500/25"
            onClick={handleSave}
            disabled={saving || !displayName.trim()}
            data-testid="display-name-submit-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save & Continue →"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DisplayNameModal;
